import {MemoryNode, patchGraph, rebuildGraph} from './graph.ts';
import {KDTree} from './kd-tree.ts';
import type {Memory, MemoryRef, MemoryStore} from './memory.ts';
import {cosineDistance, embedMemoryFields} from '../utils.ts';

const TREE_TOMBSTONE_LIMIT = 0.25;

export function memoryStore(memories: MemoryStore): {
	list: Memory[];
	cache: MemoryCache | null;
	find: (name: string) => Memory | undefined;
	ghosts: () => string[];
	search: (vector: number[], limit: number) => MemoryRef[];
	forget: (name: string) => boolean;
	rebuild: (changed?: Memory[]) => MemoryNode[];
	backfillEmbeddings: (llm: any) => Promise<number>;
} {
	if(memories instanceof MemoryCache) {
		return {
			list: memories.memories,
			cache: memories,
			find: name => memories.find(name),
			ghosts: () => memories.ghosts(),
			search: (vector, limit) => memories.search(vector, limit),
			forget: name => memories.remove(name),
			rebuild: changed => memories.rebuild(changed),
			backfillEmbeddings: llm => memories.backfillEmbeddings(llm),
		};
	}

	return {
		list: memories,
		cache: null,
		find: name => memories.find(m => m.name === name),
		ghosts: () => rebuildGraph(memories).filter(n => n.missing).map(n => n.name),
		search: (vector, limit) => memories
			.filter(m => m.embedding?.length)
			.map(m => ({
				name: m.name,
				description: m.description,
				distance: cosineDistance(vector, m.embedding),
			}))
			.sort((a, b) => a.distance - b.distance)
			.slice(0, limit),
		forget: name => {
			const idx = memories.findIndex(m => m.name === name);
			if(idx === -1) return false;

			memories.splice(idx, 1);
			return true;
		},
		rebuild: changed => rebuildGraph(memories),
		backfillEmbeddings: async llm => {
			const missing = memories.filter(m => !m.embedding?.length);
			if(!missing.length) return 0;

			await Promise.all(missing.map(async node => {
				await embedMemoryFields(node, llm);
			}));

			return missing.length;
		},
	};
}

export class MemoryCache {
	private tree!: KDTree<MemoryRef>;
	private indexed = new Map<string, number[]>();
	public memories: Memory[];
	public nodes: MemoryNode[] = [];

	get length() {
		return this.memories.length;
	}

	constructor(memories: Memory[]) {
		this.memories = memories;
		this.tree = new KDTree<MemoryRef>(0);
		this.rebuild();
	}

	find(name: string): Memory | undefined {
		return this.memories.find(m => m.name === name);
	}

	private syncTree(): void {
		const current = new Set(this.memories.map(m => m.name));

		for(const [name, emb] of [...this.indexed]) {
			const mem = this.memories.find(m => m.name === name);

			if(!mem || !current.has(name) || mem.embedding !== emb) {
				this.tree.remove(p => p.name === name);
				this.indexed.delete(name);
			}
		}

		for(const mem of this.memories) {
			if(!mem.embedding?.length || this.indexed.has(mem.name)) continue;

			if(this.tree.dims === 0) {
				this.tree = new KDTree<MemoryRef>(mem.embedding.length, 'cosine');
			}

			if(mem.embedding.length !== this.tree.dims) continue;

			this.tree.insert({
				vector: mem.embedding,
				payload: {
					name: mem.name,
					description: mem.description,
				},
			});

			this.indexed.set(mem.name, mem.embedding);
		}

		if(this.tree.tombstoneRatio > TREE_TOMBSTONE_LIMIT) {
			this.tree.rebalance();
		}
	}

	search(query: number[], limit: number): MemoryRef[] {
		if(!this.tree || this.tree.dims === 0) return [];

		return this.tree.knn(query, limit).map(r => ({
			...r.point.payload,
			distance: r.distance,
		}));
	}

	add(memory: Memory): void {
		this.memories.push(memory);
		this.rebuild([memory]);
	}

	update(memory: Memory): void {
		const existing = this.find(memory.name);

		if(existing) Object.assign(existing, memory);
		else this.memories.push(memory);

		this.rebuild([existing ?? memory]);
	}

	remove(name: string): boolean {
		const idx = this.memories.findIndex(m => m.name === name);
		if(idx === -1) return false;

		this.memories.splice(idx, 1);
		this.rebuild();
		return true;
	}

	ghosts(): string[] {
		return this.nodes.filter(n => n.missing).map(n => n.name);
	}

	rebuild(changed?: Memory[]): MemoryNode[] {
		this.nodes = changed?.length && this.nodes.length
			? patchGraph(this.memories, this.nodes, changed)
			: rebuildGraph(this.memories);

		this.syncTree();
		return this.nodes;
	}

	commit(changed?: Memory[]): MemoryNode[] {
		return this.rebuild(changed);
	}

	async backfillEmbeddings(llm: any): Promise<number> {
		const missing = this.memories.filter(m => !m.embedding?.length);
		if(!missing.length) return 0;

		await Promise.all(missing.map(node => embedMemoryFields(node, llm)));
		this.commit(missing);

		return missing.length;
	}
}
